import { useEffect, useState, useCallback } from "react";

/**
 * Hook para instalação do PWA.
 *
 * - canInstall: true quando o navegador disparou `beforeinstallprompt` e o
 *   app ainda não está instalado (funciona principalmente no Android/Chrome/Edge).
 * - isStandalone: true quando a página já está rodando em modo standalone
 *   (usuário abriu pelo ícone da home).
 * - isIOS: utilitário para mostrar instruções manuais no iOS, que não suporta
 *   `beforeinstallprompt`.
 * - promptInstall(): dispara o prompt nativo. Retorna true se aceito.
 */
export function useInstallPrompt() {
    const [promptEvent, setPromptEvent] = useState(null);
    const [isStandalone, setIsStandalone] = useState(false);

    useEffect(() => {
        const check = () => {
            const standalone =
                window.matchMedia?.("(display-mode: standalone)")?.matches ||
                window.navigator.standalone === true;
            setIsStandalone(!!standalone);
        };
        check();

        const onBefore = (e) => {
            e.preventDefault();
            setPromptEvent(e);
        };
        const onInstalled = () => {
            setPromptEvent(null);
            setIsStandalone(true);
        };

        window.addEventListener("beforeinstallprompt", onBefore);
        window.addEventListener("appinstalled", onInstalled);
        return () => {
            window.removeEventListener("beforeinstallprompt", onBefore);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    const promptInstall = useCallback(async () => {
        if (!promptEvent) return false;
        try {
            promptEvent.prompt();
            const choice = await promptEvent.userChoice;
            if (choice?.outcome === "accepted") {
                setPromptEvent(null);
                return true;
            }
        } catch {
            /* ignora */
        }
        return false;
    }, [promptEvent]);

    const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;

    return {
        canInstall: !!promptEvent && !isStandalone,
        isStandalone,
        isIOS: isIOS && !isStandalone,
        promptInstall,
    };
}
